import { TReferralCredits } from "@/components/Checkout/handlers";
import { PAYMENT_V2_ENDPOINT_V2 } from "@/components/Pricing/v2/constants.pricing";
import { TBillingInterval, TCloudAPIResponse } from "@/types/app";
import { applyLocalizedAmount } from "@/utils/applyLocalizedAmount";
import axios from "axios";

const APIURL = `${PAYMENT_V2_ENDPOINT_V2}/plan/change-interval`;

export interface EstimateIntervalPayload {
  change_to: TBillingInterval;
  estimate: true;
  discounts?: string[];
}

export interface EstimateIntervalData {
  // The currency every amount below is in; absent means USD cents. Pass it to
  // `formatMinorAmount`, which picks the divisor off it.
  currency?: string;

  amount_due: number;
  amount_due_with_referral_credits?: number;
  sub_total?: number;
  tax_amount?: number;
  tax_rate_percentage?: number;
  next_billing_date?: number;
  unused_days?: number;
  referral_credits?: TReferralCredits;
  message: string;
  discounts:
    | [
        {
          coupon_code: string; // applied coupon
          percent_off: number | null; //
          amount_off: number | null; //
          invalid: boolean; //
          invalid_reason: string; //
        },
      ]
    | [];

  // Absent on the V1 path.
  plan_amount?: number;
  prorated_amount?: number;
  discount_total_amount?: number;
  credit_amount?: number;

  /** Absent outside a localised market. */
  localized_amount?: {
    currency: string; // "INR"  //-- Additional
    symbol: string; // "₹"  //-- Additional
    amount_due: number;
    amount_due_with_referral_credits: number;
    tax_amount: number;
    sub_total_excluding_tax: number;
    sub_total: number;
    discounts:
      | [
          {
            coupon_code: string;
            percent_off: number | null;
            amount_off: number | null;
            invalid: boolean;
            invalid_reason: string;
          },
        ]
      | [];
    coupon_discount_applied: number;
    aftershoot_credits_applied: number;
    /** `null` when the user has no referral credits. */
    referral_credits: TReferralCredits | null;

    rate: number; // 95.95639741301552 -- Additional
    markup_percent: number; //-- Additional
  };
}

type TResponse = TCloudAPIResponse<EstimateIntervalData>;

interface EstimateIntervalOption {
  token: string;
  payload: EstimateIntervalPayload;
  isLocalisedPricing?: boolean;
}

export async function estimateIntervalChange(
  option: EstimateIntervalOption,
): Promise<TResponse> {
  const headers = {
    Authorization: "Bearer " + option.token,
    "Content-Type": "application/json",
  };

  try {
    const response = await axios.post(APIURL, option.payload, {
      headers,
    });

    const resData = response.data as TResponse;

    if (!resData.data) return resData;

    return {
      ...resData,
      data: applyLocalizedAmount(
        resData.data,
        "interval estimate",
        (localized) => ({
          amount_due: localized.amount_due,
          sub_total: localized.sub_total,
          tax_amount: localized.tax_amount,
          amount_due_with_referral_credits:
            localized.amount_due_with_referral_credits,
          discounts: localized.discounts ?? [],
          // Dropped rather than left as-is when absent: the USD credits must
          // not survive into a localised estimate.
          referral_credits: localized.referral_credits ?? undefined,
        }),
        option.isLocalisedPricing,
      ),
    };
  } catch (error: any) {
    console.error("Error estimating interval change:", error);
    throw error;
  }
}
